// lib
import { useContext, useState, useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import cookie from 'react-cookies'

import { DataContext as SidebarDataContext } from '@aytm/app/stats/page/sidebar/components/context'

import useNonInitialEffect from '@aytm/app/stats/page/components/hooks/useNonInitialEffect'
import useApplyFilters from '@aytm/app/stats/page/sidebar/components/hooks/useApplyFilters'

import PageGlobals from '@aytm/app/stats/lib/page/page-globals'

import { isFilterApplied } from '@aytm/app/stats/page/components/lib/utils'
import { toggleCheckbox } from '@aytm/app/stats/page/sidebar/components/lib/utils'

// components
import { Button } from 'primereact/button'
import { RadioButton } from 'primereact/radiobutton'
import { Checkbox } from 'primereact/checkbox'
import Popup from '@aytm/lib/rfd-ui/components/basic/popup'

// styles
import style from '@aytm/app/stats/page/sidebar/export/styles/ExportPanel.module.scss'

const COOKIE_PREFIX = 'AYTM_SURVEY_EXPORT'

// ['export_csv', 'export_excel_coded_map', 'export_excel', 'export_spss', 'export_jmp'].includes(id)

const ResponseLevelData = ({
  survey,
  model,
  report,
  features,
  isOeExists,
  processing,
  disabled,
  onExport,
  onParamsChange
}) => {
  const { sidebarState, sidebarDispatch } = useContext(SidebarDataContext)

  // an Xpert report takes the same row-level restrictions as a survey whose raw data is off
  const isRawDataOff = 'off' == features.individual_data.status?.raw || Boolean(model.isReportXpert)

  const isExcelDisabled =
    !PageGlobals.USER_ID || isRawDataOff || report.current.scripts?.info?.disable_excel_summary || false
  const isExcelNumbersDisabled = isRawDataOff || features.export_excel_coded_map.disabled || false
  const isSpssDisabled = isRawDataOff || features.export_spss.disabled || false
  const isJmpDisabled = isRawDataOff || features.export_jmp.disabled || false
  const isCsvDisabled = isRawDataOff

  const [fileType, setFileType] = useState(
    sidebarState.export.response.fileType ||
      (isExcelDisabled
        ? isExcelNumbersDisabled
          ? isSpssDisabled
            ? isJmpDisabled
              ? isCsvDisabled
                ? null
                : 'csv'
              : 'jmp'
            : 'spss'
          : 'excel_numbers'
        : 'excel')
  )

  // The box asks to INCLUDE the question text and starts ticked; the cookie keeps the box's own state
  const [shortLabels, setShortLabels] = useState('false' != cookie.load(`${COOKIE_PREFIX}_short-labels`))

  const [clExportVisibility, setClExportVisibility] = useState('hide' == model.clExportVisibility)
  const [ignoreTags, setIgnoreTags] = useState(model.ignoreTagsExport)

  const [applyFilters, setApplyFilters] = useApplyFilters(sidebarState.export?.response, model.filtersPattern)
  const [dataQualityExport, setDataQualityExport] = useState(
    Object.hasOwn(sidebarState, 'export') && Object.hasOwn(sidebarState.export.response, 'dataQualityExport')
      ? sidebarState.export.response.dataQualityExport
      : model.dataQualityExport
  )
  // File B (rejects) is raw/mapped only, so it lives on this row-level panel and never on Summary/Presentation
  const [dataQualityExportRejects, setDataQualityExportRejects] = useState(
    Object.hasOwn(sidebarState, 'export') && Object.hasOwn(sidebarState.export.response, 'dataQualityExportRejects')
      ? sidebarState.export.response.dataQualityExportRejects
      : false
  )

  const onShortLabelsChange = value => {
    const date = new Date()
    date.setDate(date.getDate() + 180)

    cookie.save(`${COOKIE_PREFIX}_short-labels`, String(value), {
      path: '/',
      expires: date
    })

    setShortLabels(value)
  }

  const onExportClick = useCallback(
    event => {
      'function' == typeof onExport &&
        onExport({
          fileType,
          exportType: ['spss', 'excel_numbers', 'jmp'].includes(fileType) ? 'mapped' : 'raw',
          props: { noCache: Boolean(event.shiftKey), clExportVisibility, applyFilters },
          options: {
            sensitive: 'MacIntel' == navigator.platform ? event.metaKey : event.ctrlKey,
            // the service reads the opposite flag: short labels leave the question text out
            shortLabels: !shortLabels,
            ignoreTags,
            dataQualityExport,
            dataQualityExportRejects
          }
        })
    },
    [
      fileType,
      clExportVisibility,
      applyFilters,
      shortLabels,
      ignoreTags,
      dataQualityExport,
      dataQualityExportRejects,
      onExport
    ]
  )

  useEffect(
    () =>
      sidebarDispatch({
        type: 'UPDATE_STATE',
        key: 'export',
        state: {
          response: {
            ...sidebarState.export.response,
            fileType,
            applyFilters,
            // the filter the box was set against, so a later open keeps a hand-set tick only under it
            filtersPattern: model.filtersPattern,
            dataQualityExport,
            dataQualityExportRejects
          }
        }
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fileType, applyFilters, model.filtersPattern, dataQualityExport, dataQualityExportRejects]
  )

  useNonInitialEffect(
    () =>
      onParamsChange(
        {
          cl_export_visibility: clExportVisibility ? 'hide' : 'show',
          ignore_tags_export: ignoreTags
        },
        false
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [clExportVisibility, ignoreTags, onParamsChange]
  )

  // useEffect(() => {}, [sidebarState.export.response])

  // PME2 (AYTM-11321): an `available` format keeps its (disabled) control but gains the upgrade
  // popup — server-authored `text`, guarded icon, upgrade link — mirroring CustomQuestionButton.
  const upsellOption = (feature, colClassName, control) =>
    feature.available ? (
      <Popup
        className="rfd-ui-popup-warning"
        targetClassName={`${style.param} ${colClassName}`}
        width="254px"
        trigger="hover"
        direction="topleft"
        portal
        content={() => (
          <div>
            <p>
              {feature.onPackage.icon ? (
                <span className={`stat-upgrade-icon stat-upgrade-icon-${feature.onPackage.id}`}>
                  <i className={`icon-aytmfd-${feature.onPackage.icon}`} />
                </span>
              ) : null}
              <span dangerouslySetInnerHTML={{ __html: feature.onPackage.text }} />
            </p>
          </div>
        )}
      >
        <a href={feature.onPackage.upgradeURL} rel="noreferrer">
          {control}
        </a>
      </Popup>
    ) : (
      <div className={`${style.param} ${colClassName}`}>{control}</div>
    )

  return (
    <>
      <div className={style.section}>
        <div className={style['section-title']}>
          <b>File types</b>
        </div>

        <div className={`${style.row} ${style['row-indent']}`}>
          <div className={`${style.param} p-col-5`}>
            <RadioButton
              data-testid="stat-exports-response-excel"
              className="p-radiobutton-sm"
              aria-labelledby="stat-exports-response-excel-label"
              value="excel"
              checked={['excel', 'excel_numbers'].includes(fileType)}
              disabled={(isExcelDisabled && isExcelNumbersDisabled) || disabled}
              onChange={
                (isExcelDisabled && isExcelNumbersDisabled) || disabled
                  ? null
                  : () => setFileType(isExcelNumbersDisabled || disabled ? 'excel' : 'excel_numbers')
              }
            />
            <span
              data-testid="stat-exports-response-excel-label"
              id="stat-exports-response-excel-label"
              className={classNames(style['row-label'], style['row-label-sm'], {
                'p-disabled': (isExcelDisabled && isExcelNumbersDisabled) || disabled
              })}
              onClick={
                (isExcelDisabled && isExcelNumbersDisabled) || disabled
                  ? null
                  : () => setFileType(isExcelNumbersDisabled ? 'excel' : 'excel_numbers')
              }
            >
              Excel
            </span>
          </div>

          {features.export_spss.hidden
            ? null
            : upsellOption(
                features.export_spss,
                'p-col-5',
                <>
                  <RadioButton
                    data-testid="stat-exports-response-spss"
                    className="p-radiobutton-sm"
                    aria-labelledby="stat-exports-response-spss-label"
                    value="spss"
                    checked={'spss' == fileType}
                    disabled={isSpssDisabled || disabled}
                    onChange={isSpssDisabled || disabled ? null : () => setFileType('spss')}
                  />
                  <span
                    data-testid="stat-exports-response-spss-label"
                    id="stat-exports-response-spss-label"
                    className={classNames(style['row-label'], style['row-label-sm'], {
                      'p-disabled': isSpssDisabled || disabled
                    })}
                    onClick={isSpssDisabled || disabled ? null : () => setFileType('spss')}
                  >
                    SPSS
                  </span>
                </>
              )}
        </div>
        <div className={`${style.row} ${style['row-indent']}`}>
          <div className={`${style.param} p-col-5`}>
            <RadioButton
              data-testid="stat-exports-response-csv"
              className="p-radiobutton-sm"
              aria-labelledby="stat-exports-response-csv-label"
              value="csv"
              checked={'csv' == fileType}
              disabled={isCsvDisabled || disabled}
              onChange={isCsvDisabled || disabled ? null : () => setFileType('csv')}
            />
            <span
              data-testid="stat-exports-response-csv-label"
              id="stat-exports-response-csv-label"
              className={classNames(style['row-label'], style['row-label-sm'], {
                'p-disabled': isCsvDisabled || disabled
              })}
              onClick={isCsvDisabled || disabled ? null : () => setFileType('csv')}
            >
              CSV
            </span>
          </div>

          {features.export_jmp.hidden
            ? null
            : upsellOption(
                features.export_jmp,
                'p-col-5',
                <>
                  <RadioButton
                    data-testid="stat-exports-response-jmp"
                    className="p-radiobutton-sm"
                    aria-labelledby="stat-exports-response-jmp-label"
                    value="jmp"
                    checked={'jmp' == fileType}
                    disabled={isJmpDisabled || disabled}
                    onChange={isJmpDisabled || disabled ? null : () => setFileType('jmp')}
                  />
                  <span
                    data-testid="stat-exports-response-jmp-label"
                    id="stat-exports-response-jmp-label"
                    className={classNames(style['row-label'], style['row-label-sm'], {
                      'p-disabled': isJmpDisabled || disabled
                    })}
                    onClick={isJmpDisabled || disabled ? null : () => setFileType('jmp')}
                  >
                    JMP
                  </span>
                </>
              )}
        </div>
      </div>

      {['excel', 'excel_numbers'].includes(fileType) ? (
        <div className={style.section}>
          <div className={style['section-title']}>
            <b>Data type</b>
          </div>

          <div className={`${style.row} ${style['row-indent']}`}>
            {upsellOption(
              features.export_excel_coded_map,
              'p-col-12',
              <>
                <RadioButton
                  data-testid="stat-exports-response-excel_numbers"
                  className="p-radiobutton-sm"
                  aria-labelledby="stat-exports-response-excel_numbers-label"
                  value="excel_numbers"
                  checked={'excel_numbers' == fileType}
                  disabled={disabled || features.export_excel_coded_map.available}
                  onChange={
                    disabled || features.export_excel_coded_map.available ? null : () => setFileType('excel_numbers')
                  }
                />
                <span
                  data-testid="stat-exports-response-excel_numbers-label"
                  id="stat-exports-response-excel_numbers-label"
                  className={classNames(style['row-label'], style['row-label-sm'], {
                    'p-disabled': disabled || features.export_excel_coded_map.available
                  })}
                  onClick={
                    disabled || features.export_excel_coded_map.available ? null : () => setFileType('excel_numbers')
                  }
                >
                  Numerically coded with labels
                </span>
              </>
            )}
          </div>
          <div className={`${style.row} ${style['row-indent']}`}>
            <div className={`${style.param} p-col-12`}>
              <RadioButton
                data-testid="stat-exports-response-excel_raw"
                className="p-radiobutton-sm"
                aria-labelledby="stat-exports-response-excel_raw-label"
                value="excel_raw"
                checked={'excel' == fileType}
                disabled={disabled}
                onChange={disabled ? null : () => setFileType('excel')}
              />
              <span
                data-testid="stat-exports-response-excel_raw-label"
                id="stat-exports-response-excel_raw-label"
                className={classNames(style['row-label'], style['row-label-sm'], {
                  'p-disabled': disabled
                })}
                onClick={disabled ? null : () => setFileType('excel')}
              >
                Answer text
              </span>
            </div>
          </div>
        </div>
      ) : null}

      <div className={style.section}>
        {'jmp' == fileType ? (
          <div className={`${style.row} ${style['row-indent']}`}>
            <div className={`${style.param} p-col-12`}>
              <Checkbox
                data-testid="stat-exports-response-short-labels"
                className="p-checkbox-sm"
                aria-labelledby="stat-exports-response-short-labels-label"
                value="short-labels"
                checked={shortLabels}
                disabled={disabled}
                onChange={disabled ? null : () => onShortLabelsChange(!shortLabels)}
                onKeyDown={disabled ? null : event => toggleCheckbox(event, () => onShortLabelsChange(!shortLabels))}
              />
              <span
                data-testid="stat-exports-response-short-labels-label"
                id="stat-exports-response-short-labels-label"
                className={classNames(style['row-label'], style['row-label-sm'], { 'p-disabled': disabled })}
                onClick={disabled ? null : () => onShortLabelsChange(!shortLabels)}
              >
                Include question text in the variable name
              </span>
            </div>
          </div>
        ) : null}
        {survey?.cl_exists ? (
          <div className={`${style.row} ${style['row-indent']}`}>
            <div className={`${style.param} p-col-12`}>
              <Checkbox
                data-testid="stat-exports-response-cl-export-visibility"
                className="p-checkbox-sm"
                aria-labelledby="stat-exports-response-cl-export-visibility-label"
                value="cl-export-visibility"
                checked={clExportVisibility}
                disabled={disabled}
                onChange={disabled ? null : () => setClExportVisibility(!clExportVisibility)}
                onKeyDown={
                  disabled ? null : event => toggleCheckbox(event, () => setClExportVisibility(!clExportVisibility))
                }
              />
              <span
                data-testid="stat-exports-response-cl-export-visibility-label"
                id="stat-exports-response-cl-export-visibility-label"
                className={classNames(style['row-label'], style['row-label-sm'], { 'p-disabled': disabled })}
                onClick={disabled ? null : () => setClExportVisibility(!clExportVisibility)}
              >
                Hide aytm Logic
              </span>
            </div>
          </div>
        ) : null}
        {isFilterApplied(model.filtersPattern) ? (
          <div className={`${style.row} ${style['row-indent']}`}>
            <div className={`${style.param} p-col-12`}>
              <Checkbox
                data-testid="stat-exports-response-apply-filters"
                className="p-checkbox-sm"
                aria-labelledby="stat-exports-response-apply-filters-label"
                value="apply-filters"
                checked={applyFilters}
                disabled={disabled}
                onChange={disabled ? null : () => setApplyFilters(!applyFilters)}
                onKeyDown={disabled ? null : event => toggleCheckbox(event, () => setApplyFilters(!applyFilters))}
              />
              <span
                data-testid="stat-exports-response-apply-filters-label"
                id="stat-exports-response-apply-filters-label"
                className={classNames(style['row-label'], style['row-label-sm'], { 'p-disabled': disabled })}
                onClick={disabled ? null : () => setApplyFilters(!applyFilters)}
              >
                Apply active filters
              </span>
            </div>
          </div>
        ) : null}
        {isOeExists ? (
          <div className={`${style.row} ${style['row-indent']}`}>
            <div className={`${style.param} p-col-12`}>
              <Checkbox
                data-testid="stat-exports-response-ignore-tags"
                className="p-checkbox-sm"
                aria-labelledby="stat-exports-response-ignore-tags-label"
                value="ignore-tags"
                checked={ignoreTags}
                disabled={disabled}
                onChange={disabled ? null : () => setIgnoreTags(!ignoreTags)}
                onKeyDown={disabled ? null : event => toggleCheckbox(event, () => setIgnoreTags(!ignoreTags))}
              />
              <span
                data-testid="stat-exports-response-ignore-tags-label"
                id="stat-exports-response-ignore-tags-label"
                className={classNames(style['row-label'], style['row-label-sm'], { 'p-disabled': disabled })}
                onClick={disabled ? null : () => setIgnoreTags(!ignoreTags)}
              >
                Exclude OE Code Columns
              </span>
            </div>
          </div>
        ) : null}
        {!PageGlobals.USER_ID ||
        (PageGlobals.SHARE_KEY != undefined && PageGlobals.SHARE_KEY.key != undefined) ||
        features.data_quality_export.disabled ? null : (
          <div className={`${style.row} ${style['row-indent']}`}>
            <div className={`${style.param} p-col-12`}>
              <Checkbox
                data-testid="stat-exports-response-data-quality-export"
                className="p-checkbox-sm"
                aria-labelledby="stat-exports-response-data-quality-export-label"
                value="data-quality-export"
                checked={dataQualityExport}
                disabled={disabled}
                onChange={disabled ? null : () => setDataQualityExport(!dataQualityExport)}
                onKeyDown={
                  disabled ? null : event => toggleCheckbox(event, () => setDataQualityExport(!dataQualityExport))
                }
              />
              <span
                data-testid="stat-exports-response-data-quality-export-label"
                id="stat-exports-response-data-quality-export-label"
                className={classNames(style['row-label'], style['row-label-sm'], { 'p-disabled': disabled })}
                onClick={disabled ? null : () => setDataQualityExport(!dataQualityExport)}
              >
                Respondent Detail Export
              </span>
            </div>
          </div>
        )}
        {!PageGlobals.USER_ID ||
        (PageGlobals.SHARE_KEY != undefined && PageGlobals.SHARE_KEY.key != undefined) ||
        features.data_quality_export_rejects.disabled ? null : (
          <div className={`${style.row} ${style['row-indent']}`}>
            <div className={`${style.param} p-col-12`}>
              <Checkbox
                data-testid="stat-exports-response-data-quality-export-rejects"
                className="p-checkbox-sm"
                aria-labelledby="stat-exports-response-data-quality-export-rejects-label"
                value="data-quality-export-rejects"
                checked={dataQualityExportRejects}
                disabled={disabled}
                onChange={disabled ? null : () => setDataQualityExportRejects(!dataQualityExportRejects)}
                onKeyDown={
                  disabled
                    ? null
                    : event => toggleCheckbox(event, () => setDataQualityExportRejects(!dataQualityExportRejects))
                }
              />
              <span
                data-testid="stat-exports-response-data-quality-export-rejects-label"
                id="stat-exports-response-data-quality-export-rejects-label"
                className={classNames(style['row-label'], style['row-label-sm'], { 'p-disabled': disabled })}
                onClick={disabled ? null : () => setDataQualityExportRejects(!dataQualityExportRejects)}
              >
                Data Quality Export (Rejected)
              </span>
            </div>
          </div>
        )}
      </div>

      <div className={style.section}>
        <div className={style.row}>
          <Button
            data-testid="stat-exports-response-export"
            className="p-button-rounded p-button-blue"
            label={'response' == processing ? 'Downloading' : 'Download file'}
            loading={'response' == processing}
            disabled={null == fileType || null !== processing || disabled}
            onClick={null == fileType || null !== processing || disabled ? null : onExportClick}
          />
        </div>

        {!report.current.schema.length ? (
          <div className={`${style.row} ${style['row-error']}`}>
            You need at least 1 question in order to use these features
          </div>
        ) : null}
      </div>
    </>
  )
}

ResponseLevelData.propTypes = {
  survey: PropTypes.object,
  model: PropTypes.object,
  report: PropTypes.object,
  features: PropTypes.object,
  isOeExists: PropTypes.bool,
  processing: PropTypes.string,
  disabled: PropTypes.bool,
  onExport: PropTypes.func,
  onParamsChange: PropTypes.func
}

ResponseLevelData.displayName = 'ResponseLevelData'

export default ResponseLevelData
